import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead, RowActions } from '@/components/table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ProcessStepPicker } from './ProcessStepPicker'
import { useMethodDefinitions } from '@/hooks/useResourceCapabilities'
import type {
  CapabilityOptions,
  MethodRow,
  MethodStepRow,
} from '@/api/resourceCapabilitiesApi'

/**
 * The METHODS: each a named collection of process steps.
 *
 * "In each procedure we can have multiple processes — it's about which processes
 * one is capable to perform. A method has processes, and a person can be given a
 * method they're capable of."
 *
 * So this grid defines what a qualification MEANS, and the Qualifications grid
 * grants it. A method was previously just a label in an option set, which named
 * a thing without ever saying what it covered.
 */
// Per-user column layout. Method is locked (it names the definition) and so is
// the actions cell. Both this grid's header and MethodDefinitionRow below call
// the hook with this id, so they read one layout.
// See src/components/table/index.ts for the pattern.
const METHOD_DEFS_TABLE_ID = 'method-definitions'

const METHOD_DEFS_COLUMNS: TableColumnDef[] = [
  { id: 'method', header: 'Method', locked: true, sortable: true },
  { id: 'processes', header: 'Processes', sortable: true },
  { id: 'qualified', header: 'Qualified', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Column sorting (z8nrz7e4h6). Processes sort by the chips as read, in order.
const METHOD_DEFS_SORT: SortAccessors<MethodRow> = {
  method: r => r.name,
  processes: r => r.steps.map(s => s.label ?? s.fieldId).join(', '),
  qualified: r => r.resourceCount,
}

/* Percentages kept rather than pixel defaults — they keep filling the panel. */
const METHOD_DEF_HEAD_CLASSES: Record<string, string> = {
  method: 'w-[26%]',
  processes: 'w-[44%]',
  qualified: 'w-[14%] text-right',
  actions: 'w-[16%]',
}

export function MethodDefinitionGrid({
  canManage,
  options,
  loading,
}: {
  canManage: boolean
  options: CapabilityOptions | undefined
  loading: boolean
}) {
  const cols = useTableColumns(METHOD_DEFS_TABLE_ID, METHOD_DEFS_COLUMNS)
  const { list, create, update, remove } = useMethodDefinitions()
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const rows = useSortedRows(list.data, cols.sort, METHOD_DEFS_SORT)
  const processSteps = options?.processSteps ?? []

  return (
    <Card>
      <CardContent className='pt-6'>
        <h3 className='text-sm font-semibold text-foreground'>Methods</h3>
        <p className='mb-4 mt-0.5 text-sm text-muted-foreground'>
          Each method is a set of processes.
        </p>

        {canManage && (
          <div className='mb-4'>
            <Button
              onClick={() => {
                setError(null)
                setEditingId(null)
                setAdding(true)
              }}
              disabled={adding}
              className='gap-1.5'
            >
              <Plus className='h-4 w-4' />
              Add
            </Button>
          </div>
        )}
        {error && <p className='mb-3 text-sm font-medium text-red-600'>{error}</p>}

        {/* Nothing to build a method OUT of. Said here rather than left as an
            empty picker inside every row: the fix is authoring a Process field
            on a form, which is somewhere else entirely. */}
        {!loading && processSteps.length === 0 && (
          <div className='mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900'>
            No form has a <span className='font-medium'>Process</span> field yet. Add one in the
            form builder first.
          </div>
        )}

        <div className='overflow-x-auto rounded-lg border border-gray-200'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={METHOD_DEF_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {adding && (
                <MethodDefinitionRow
                  row={DRAFT_METHOD}
                  canManage={canManage}
                  editing
                  confirming={false}
                  processSteps={processSteps}
                  saveLabel='Add'
                  onEdit={() => {}}
                  onCancel={() => setAdding(false)}
                  onSave={async payload => {
                    setError(null)
                    try {
                      await create.mutateAsync(payload)
                      setAdding(false)
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Could not add that method.')
                    }
                  }}
                  onAskRemove={() => {}}
                  onCancelRemove={() => {}}
                  onRemove={async () => {}}
                />
              )}
              {loading || list.isLoading ? (
                <EmptyRow span={cols.visible.length}>Loading…</EmptyRow>
              ) : rows.length === 0 ? (
                adding ? null : <EmptyRow span={cols.visible.length}>No methods yet.</EmptyRow>
              ) : (
                rows.map(row => (
                  <MethodDefinitionRow
                    key={row.id}
                    row={row}
                    canManage={canManage}
                    editing={editingId === row.id}
                    confirming={confirmingId === row.id}
                    processSteps={processSteps}
                    onEdit={() => {
                      setEditingId(row.id)
                      setConfirmingId(null)
                    }}
                    onCancel={() => setEditingId(null)}
                    onSave={async payload => {
                      setError(null)
                      try {
                        await update.mutateAsync({ id: row.id, payload })
                        setEditingId(null)
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Could not save that method.')
                      }
                    }}
                    onAskRemove={() => setConfirmingId(row.id)}
                    onCancelRemove={() => setConfirmingId(null)}
                    onRemove={async () => {
                      setError(null)
                      try {
                        await remove.mutateAsync(row.id)
                        setConfirmingId(null)
                      } catch (e) {
                        // The server refuses while anyone holds it, and says how
                        // many — which is the whole point of the message.
                        setError(e instanceof Error ? e.message : 'Could not delete that method.')
                        setConfirmingId(null)
                      }
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

/** The unsaved row. See the note on the other grids' drafts. */
const DRAFT_METHOD: MethodRow = {
  id: '',
  name: '',
  description: null,
  steps: [],
  resourceCount: 0,
}

function MethodDefinitionRow({
  row,
  canManage,
  editing,
  confirming,
  processSteps,
  saveLabel = 'Save',
  onEdit,
  onCancel,
  onSave,
  onAskRemove,
  onCancelRemove,
  onRemove,
}: {
  row: MethodRow
  canManage: boolean
  editing: boolean
  confirming: boolean
  processSteps: CapabilityOptions['processSteps']
  saveLabel?: string
  onEdit: () => void
  onCancel: () => void
  onSave: (payload: { name: string; fieldIds: string[] }) => Promise<void>
  onAskRemove: () => void
  onCancelRemove: () => void
  onRemove: () => Promise<void>
}) {
  // Same table id as the grid header, so the row and the header stay in step.
  const cols = useTableColumns(METHOD_DEFS_TABLE_ID, METHOD_DEFS_COLUMNS)
  const [draftName, setDraftName] = useState(row.name)
  const [draftSteps, setDraftSteps] = useState<string[]>(row.steps.map(s => s.fieldId))

  if (editing) {
    return (
      <TableRow>
        <ColumnCells
          columns={cols}
          cells={{
            method: (
            <TableCell>
              <Input
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                placeholder='Method name'
              />
            </TableCell>
            ),
            processes: (
            <TableCell>
              <ProcessStepPicker options={processSteps} value={draftSteps} onChange={setDraftSteps} />
            </TableCell>
            ),
            qualified: <TableCell className='text-right text-muted-foreground'>—</TableCell>,
            actions: (
            <TableCell className='text-right'>
              <div className='flex justify-end gap-2'>
                <Button variant='outline' size='sm' onClick={onCancel}>
                  Cancel
                </Button>
                {/* A method needs a name; its processes can come later, since a
                    method with none is legible (it qualifies nobody for anything
                    yet) while a nameless one is not. */}
                <Button
                  size='sm'
                  disabled={!draftName.trim()}
                  onClick={() => void onSave({ name: draftName.trim(), fieldIds: draftSteps })}
                >
                  {saveLabel}
                </Button>
              </div>
            </TableCell>
            ),
          }}
        />
      </TableRow>
    )
  }

  return (
    <TableRow>
      <ColumnCells
        columns={cols}
        cells={{
          method: <TableCell className='font-medium'>{row.name}</TableCell>,
          processes: (
          <TableCell>
            <StepChips steps={row.steps} />
          </TableCell>
          ),
          qualified: <TableCell className='text-right tabular-nums'>{row.resourceCount}</TableCell>,
          actions: (
          <TableCell className='text-right'>
            <RowActions className='gap-2' reason='You do not have permission to manage methods'>
              {canManage &&
                (confirming ? (
                  <>
                    <Button variant='outline' size='sm' onClick={onCancelRemove}>
                      Cancel
                    </Button>
                    <Button variant='destructive' size='sm' onClick={() => void onRemove()}>
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant='ghost' size='sm' onClick={onEdit}>
                      Edit
                    </Button>
                    <Button variant='ghost' size='sm' onClick={onAskRemove}>
                      Delete
                    </Button>
                  </>
                ))}
            </RowActions>
          </TableCell>
          ),
        }}
      />
    </TableRow>
  )
}

/**
 * A method's processes, as chips.
 *
 * A step with no label is one no active form embeds any more — the field was
 * removed, or its version retired. Shown as its id rather than hidden: the
 * membership is stale, not broken, and seeing it is what lets someone remove it.
 */
function StepChips({ steps }: { steps: MethodStepRow[] }) {
  if (steps.length === 0) {
    return (
      <span className='text-sm text-muted-foreground'>No processes yet</span>
    )
  }
  return (
    <div className='flex flex-wrap gap-1'>
      {steps.map(step => (
        <span
          key={step.fieldId}
          title={step.formName ? `${step.label} — ${step.formName}` : 'No active form embeds this step'}
          className={cn(
            'rounded-full border px-2 py-0.5 text-xs',
            step.label ? 'bg-muted/50' : 'border-dashed text-muted-foreground',
          )}
        >
          {step.label ?? step.fieldId}
        </span>
      ))}
    </div>
  )
}

// `span` is passed in: the column count is a user preference now, so a stale
// constant would leave this row short of the table.
function EmptyRow({ children, span }: { children: React.ReactNode; span: number }) {
  return (
    <TableRow>
      <TableCell colSpan={span} className='py-8 text-center text-sm text-muted-foreground'>
        {children}
      </TableCell>
    </TableRow>
  )
}
